import type { RosterOfficialRow } from './rosterRows'

/** The cells the Team Officials grid allows editing in place. */
export type EditableOfficialField = 'name' | 'role' | 'email' | 'phone'

/**
 * Whether an official's email may be edited.
 *
 * Locked only once they ACCEPT — the address is then their identity, not a
 * roster field an admin may overwrite. While merely invited it stays editable,
 * so a typo'd address can be corrected and the invite resent. Same rule as
 * `canEditPlayerEmail`.
 *
 * Note this is deliberately LOOSER than the edit modal, which locks the field
 * on `isEdit && hasInitialEmail` (see `TeamOfficialModalContent`) and so also
 * locks invited officials. The grid is the intended way to fix an invited
 * official's email.
 */
export const canEditOfficialEmail = (row: RosterOfficialRow): boolean =>
  row.inviteStatus !== 'accepted'

/**
 * Whether an inline edit is worth sending. AG Grid commits on blur, so tabbing
 * through a cell would otherwise fire a PUT.
 */
export const isMeaningfulOfficialEdit = (
  field: EditableOfficialField,
  rawValue: string,
  currentValue: string
): boolean => {
  const value = (rawValue ?? '').trim()
  if (value === (currentValue ?? '').trim()) {
    return false
  }
  // An official must keep a name and a role; clearing either is a mistake.
  if ((field === 'name' || field === 'role') && value === '') {
    return false
  }
  return true
}
